import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";
import { redirectAtom, userAtom } from "../state";
import api from "../services/api";
import axios from "axios";
import Button from "../Components/Button";
import { Field, Form, Formik } from "formik";
import FormikField from "../Components/Forms/FormikField";
import { sentryService } from "../services/sentry"

const MagicLinkVerify = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const action = searchParams.get('action'); // 'accept' or 'decline'
    const [user, setUser] = useAtom(userAtom);
    const [redirect, setRedirect] = useAtom(redirectAtom);
    const [status, setStatus] = useState("verifying"); // verifying, needs_info, success, error
    const [error, setError] = useState("");
    const [organizations, setOrganizations] = useState([]);
    const [redirectInfo, setRedirectInfo] = useState(null);
    const [errorReason, setErrorReason] = useState(null); // 'expired' | 'already_used' | 'invalid'
    const [errorEmail, setErrorEmail] = useState("");
    const [resendStatus, setResendStatus] = useState("idle"); // 'idle' | 'sending' | 'sent' | 'failed'
    const hasVerifiedRef = useRef(false); // Prevent double verification (useRef persists across Strict Mode remounts)

    useEffect(() => {
        const bc = new BroadcastChannel("auth");
        bc.onmessage = (event) => {
            if (event.data.type === "logged_in") {
                window.location.href = "/";
            }
        };
        return () => bc.close();
    }, []);

    // Helper to navigate to stored redirect or default
    const navigateToDestination = async () => {
        if (redirect) {
            const destination = redirect;
            setRedirect(null);
            navigate(destination);
        } else {
            // Fetch plans and redirect to first plan's activities page
            try {
                const response = await api.getPlans();
                if (response.data.length > 0) {
                    navigate(`/plan/${response.data[0].token}/activities`);
                } else {
                    navigate("/plan/no-plans");
                }
            } catch (error) {
                console.error("Error fetching plans:", error);
                navigate("/");
            }
        }
    };

    console.log({status, user})

    useEffect(() => {
        // First, get CSRF cookie, then verify the magic link
        const verifyToken = async () => {
            // Prevent double execution (React 18 Strict Mode runs effects twice)
            if (hasVerifiedRef.current) {
                return;
            }
            hasVerifiedRef.current = true;

            try {
                // Initialize CSRF token - sanctum/csrf-cookie is at root, not under /api
                const baseUrl = import.meta.env.VITE_API_BASE.replace(/\/api\/?$/, '');
                await axios.get(baseUrl + '/sanctum/csrf-cookie', {
                    withCredentials: true
                });

                // Now verify the magic link (pass action if present)
                const response = await api.verifyMagicLink(token, action ? { action } : {});
                const newUser = response.data.user || response.data;

                console.log('Account mismatch check:', {
                    currentUser: user,
                    currentEmail: user?.email,
                    newUser: newUser,
                    newEmail: newUser?.email,
                    areDifferent: user && user.email !== newUser.email
                });

                // If user was logged in with a different account, the backend has now switched sessions
                // Just update the frontend state to match
                if (user && user.email !== newUser.email) {
                    console.log('Account mismatch detected! Backend has switched sessions. Updating frontend...');
                }

                setUser(newUser);
                sentryService.setUserContext(newUser);

                const bc = new BroadcastChannel("auth");
                bc.postMessage({ type: "logged_in" });
                bc.close();

                // Check if this is a plan invitation
                if (response.data.redirect?.type === 'plan_invitation') {
                    setRedirectInfo(response.data.redirect);
                    setStatus("success");

                    // Redirect based on action
                    setTimeout(() => {
                        if (response.data.redirect.action === 'decline') {
                            navigate("/"); // Declined, go to home
                        } else {
                            // Accepted, go to invitation page
                            navigate(`/plan/${response.data.redirect.plan_token}/invitation`);
                        }
                    }, 2000);
                } else {
                    // Regular magic link, no invitation
                    setStatus("success");
                    setTimeout(() => {
                        navigateToDestination();
                    }, 2000);
                }
            } catch (error) {
                const errorMsg = error.response?.data?.errors?.token?.[0] || error.response?.data?.message;
                const reason   = error.response?.data?.reason;
                const email    = error.response?.data?.email || "";
                console.error("Error verifying magic link:", error);

                // Token was already used and the user is already logged in as the right person —
                // this happens when they revisit a magic link URL they already clicked
                if (reason === 'already_used' && user && user.email === email) {
                    setStatus("success");
                    setTimeout(() => {
                        navigateToDestination();
                    }, 2000);
                    return;
                }

                // already_used and expired are expected user-behavior cases (re-clicking an old
                // link, or waiting too long) — don't send noise to Sentry for these
                if (reason !== 'already_used' && reason !== 'expired') {
                    sentryService.captureException(error);
                }

                // Check if this is a new user scenario
                if (error.response?.status === 404 || errorMsg?.includes("not found")) {
                    setStatus("needs_info");
                    sentryService.setTag("isNewUser", true);
                    // Load organizations for the form
                    api.getOrganizations().then(res => {
                        setOrganizations(res.data);
                    }).catch(err => {
                        console.error("Failed to load organizations", err);
                    });
                } else {
                    setStatus("error");
                    setError(errorMsg || "Failed to verify magic link");
                    setErrorReason(reason || "invalid");
                    setErrorEmail(email);
                }
            }
        };

        verifyToken();
    }, [token]);

    const handleResendLink = (values, { setErrors, setSubmitting }) => {
        setResendStatus("sending");
        api.requestMagicLink(values.email)
            .then(() => {
                setResendStatus("sent");
                setErrorEmail(values.email);
            })
            .catch(err => {
                setResendStatus("failed");
                const errors = err.response?.data?.errors;
                if (errors) setErrors(errors);
            })
            .finally(() => setSubmitting(false));
    };

    const handleSubmitUserInfo = (values, {setErrors}) => {
        api.verifyMagicLink(token, values)
            .then(response => {
                setUser(response.data);
                sentryService.setUserContext(response.data);
                setStatus("success");
                setTimeout(() => {
                    navigateToDestination();
                }, 2000);
            })
            .catch(error => {
                const errors = error.response?.data?.errors;
                sentryService.captureException(error);
                if (errors) {
                    setErrors(errors);
                } else {
                    setStatus("error");
                    setError(error.response?.data?.message || "Failed to verify magic link");
                }
            });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <img className="w-[200px] mx-auto mb-8" src="/logo.png" alt="Logo"/>
                </div>

                {status === "verifying" && (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
                        <h2 className="text-xl font-bold mb-2">Verifying your magic link...</h2>
                        <p className="text-gray-600">Please wait a moment.</p>
                    </div>
                )}

                {status === "needs_info" && (
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h2 className="text-xl font-bold mb-4">Welcome! Let's set up your account</h2>
                        <p className="text-gray-600 mb-6">Please provide some information to complete your registration.</p>

                        <Formik
                            initialValues={{name: "", organization_id: ""}}
                            onSubmit={handleSubmitUserInfo}
                        >
                            <Form>
                                <FormikField name="name" title="Full Name" instructions={[]}>
                                    <Field
                                        name="name"
                                        className="w-full p-4 border-0 rounded-md mb-4 bg-neutral-100"
                                        placeholder="Enter your full name"
                                    />
                                </FormikField>

                                {organizations.length > 0 && (
                                    <FormikField name="organization_id" title="Organization (Optional)" instructions={[]}>
                                        <Field
                                            as="select"
                                            name="organization_id"
                                            className="w-full p-4 border-0 rounded-md mb-4 bg-neutral-100"
                                        >
                                            <option value="">Select an organization</option>
                                            {organizations.map(org => (
                                                <option key={org.id} value={org.id}>{org.name}</option>
                                            ))}
                                        </Field>
                                    </FormikField>
                                )}

                                <Button type="submit">Complete Registration</Button>
                            </Form>
                        </Formik>
                    </div>
                )}

                {status === "success" && (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                        <div className="text-green-500 text-6xl mb-4">✓</div>
                        <h2 className="text-xl font-bold mb-2">
                            {redirectInfo?.action === 'decline'
                                ? 'Invitation Declined'
                                : redirectInfo
                                ? 'Successfully logged in!'
                                : 'Successfully logged in!'}
                        </h2>
                        <p className="text-gray-600">
                            {redirectInfo?.action === 'decline'
                                ? 'You have declined the invitation.'
                                : redirectInfo
                                ? `Redirecting to ${redirectInfo.plan_name}...`
                                : 'Redirecting you now...'}
                        </p>
                    </div>
                )}

                {status === "error" && (
                    <div className="bg-white rounded-lg shadow-md p-8">
                        {resendStatus === "sent" ? (
                            <div className="text-center">
                                <div className="text-green-500 text-6xl mb-4">✓</div>
                                <h2 className="text-xl font-bold mb-2">Check your email</h2>
                                <p className="text-gray-600 mb-1">
                                    We sent a new login link to <strong>{errorEmail}</strong>.
                                </p>
                                <p className="text-sm text-gray-500">
                                    The link will expire in 15 minutes.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-bold mb-2">
                                        {errorReason === "already_used"
                                            ? "Link already used"
                                            : errorReason === "expired"
                                            ? "Link expired"
                                            : "Invalid link"}
                                    </h2>
                                    <p className="text-gray-600">
                                        {errorReason === "already_used"
                                            ? "This login link has already been used. Login links are single-use for your security."
                                            : errorReason === "expired"
                                            ? "This login link has expired. Login links are valid for 15 minutes."
                                            : "This login link is no longer valid."}
                                    </p>
                                    <p className="text-gray-600 mt-2">
                                        Enter your email below and we'll send you a new one.
                                    </p>
                                </div>

                                <Formik
                                    initialValues={{ email: errorEmail }}
                                    enableReinitialize
                                    onSubmit={handleResendLink}
                                >
                                    {({ isSubmitting }) => (
                                        <Form>
                                            <FormikField name="email" title="Email" instructions={[]}>
                                                <Field
                                                    name="email"
                                                    type="email"
                                                    className="w-full p-4 border-0 rounded-md mb-4 bg-neutral-100"
                                                    placeholder="your@email.com"
                                                />
                                            </FormikField>
                                            <Button
                                                type="submit"
                                                disabled={isSubmitting || resendStatus === "sending"}
                                            >
                                                {resendStatus === "sending" ? "Sending..." : "Send me a new link"}
                                            </Button>
                                            {resendStatus === "failed" && (
                                                <p className="text-red-500 text-sm mt-2 text-center">
                                                    Failed to send — please check your email address.
                                                </p>
                                            )}
                                        </Form>
                                    )}
                                </Formik>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MagicLinkVerify;